import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { PitchAnalysis, PitchAnalysisRequest } from '../../types';
import { api } from '../../lib/api';

// Hook to fetch pitch analysis for a measurement
export function usePitchAnalysis(measurementId?: number) {
  return useQuery({
    queryKey: ['pitch-analysis', measurementId],
    queryFn: async () => {
      const response = await api.get(`/api/pitch-analyses/by_measurement/?measurement_id=${measurementId}`);
      return response.data as PitchAnalysis;
    },
    enabled: !!measurementId,
  });
}

// Hook to perform pitch analysis on a measurement
export function useAnalyzePitch() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ measurementId, data }: { measurementId: number; data: PitchAnalysisRequest }) => {
      const response = await api.post(`/api/measurements/${measurementId}/analyze_pitch/`, data);
      return response.data as PitchAnalysis;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['pitch-analysis', variables.measurementId] });
      queryClient.invalidateQueries({ queryKey: ['measurement', variables.measurementId] });
    },
  });
}

// Hook to analyze roof geometry (faces and features)
export function useAnalyzeGeometry() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (measurementId: number) => {
      const response = await api.post(`/api/measurements/${measurementId}/analyze_geometry/`, {});
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['measurement', variables] });
      queryClient.invalidateQueries({ queryKey: ['roof-faces', variables] });
      queryClient.invalidateQueries({ queryKey: ['roof-features', variables] });
    },
  });
}

// Hook to calculate materials for a measurement
export function useCalculateMaterials() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ measurementId, selections }: { measurementId: number; selections: Record<string, string> }) => {
      const response = await api.post(`/api/measurements/${measurementId}/calculate_materials/`, {
        material_selections: selections,
      });
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['material-calculations', variables.measurementId] });
    },
  });
}

// Hook to generate a measurement report
export function useGenerateReport() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ measurementId, options }: { measurementId: number; options: any }) => {
      const response = await api.post(`/api/measurements/${measurementId}/generate_report/`, options);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['measurement-reports', variables.measurementId] });
    },
  });
}

// Hook to fetch material calculations for a measurement
export function useMaterialCalculations(measurementId?: number) {
  return useQuery({
    queryKey: ['material-calculations', measurementId],
    queryFn: async () => {
      const response = await api.get(`/api/measurements/${measurementId}/material_calculations/`);
      return response.data;
    },
    enabled: !!measurementId,
  });
}

// Hook to fetch reports for a measurement
export function useMeasurementReports(measurementId?: number) {
  return useQuery({
    queryKey: ['measurement-reports', measurementId],
    queryFn: async () => {
      const response = await api.get(`/api/measurements/${measurementId}/reports/`);
      return response.data;
    },
    enabled: !!measurementId,
  });
}